import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import styles from './AOVVerticalCards.module.scss';
import {
  COLOR_ATTRIBUTES,
  SIZE_ATTRIBUTES,
} from '../../blocks/pdpV2/constants';
import { priceToString } from '../../utils';
import useDrupalSettings from '../../hooks/useDrupalSettings';
import { findOutOfStock, findSizeMatchedVariant } from '../../utils/aov';
import QuantityCounter from '../common/QuantityCounter';
import { AovCardVariant, AovDrupalSettings, AovVerticalCardProps } from './types';
import { CartInStore } from '../../slices/cart/types';
import { AOV_FLAT_RATE_TYPE, COMPLETE_YOUR_SET_TYPE } from './constants';

const AOVVerticalCards = ({
  filteredItems,
  onChangeSKUSelectedForIndex,
  selectedSKUsForProducts,
  aovType,
  quantitiesForProducts,
  onQuantityIncrement,
  onQuantityDecrement,
  mainProductAttributes,
}: AovVerticalCardProps) => {
  const { locale, formatMessage, formatNumber } = useIntl();
  const { promoBanner } = useSelector(
    (state: { cart: CartInStore }) => state.cart,
  );
  const { promotionalBanner } = useDrupalSettings() as AovDrupalSettings;

  const isFreeGiftAndQualified = useMemo(() => {
    if (
      promotionalBanner &&
      promotionalBanner?.tiers?.length === 1 &&
      promoBanner?.remainingAmount <= 0
    ) {
      return true;
    }
    return false;
  }, [promotionalBanner, promoBanner?.remainingAmount]);

  return (
    <div className={styles['flyout-container']} data-testid='flyout-container'>
      <div
        className={classNames(styles['flyout-content'], {
          [styles['apply-padding']]: aovType !== COMPLETE_YOUR_SET_TYPE,
        })}
        data-testid='flyout-content'
      >
        {filteredItems.map((item) => {
          const hasVariants = !!item?.variants && item.variants.length > 0;
          if (hasVariants) {
            const representativeVariant: AovCardVariant =
              (findSizeMatchedVariant(
                item.variants,
                mainProductAttributes,
              ) as AovCardVariant | undefined) ?? item.variants[0];
            const { price, discount } = representativeVariant;
            const haveDiscount =
              (discount && discount?.centAmount > 0) || false;
            const selectedSKUsForProduct =
              selectedSKUsForProducts[item.productId] ?? [];
            const firstUnitSKUSelected = selectedSKUsForProduct[0];
            const hasFirstUnitSelected = !!firstUnitSKUSelected;
            const size = representativeVariant.attributes.find((attr) =>
              SIZE_ATTRIBUTES.includes(attr.name),
            );
            const masterVariant = item.variants.find(
              (variant) => variant.masterVariant === true,
            );
            const isFreeGiftItem = !!(
              masterVariant?.sku === promotionalBanner?.master_variant_sku
            );
            const color =
              size?.name !== SIZE_ATTRIBUTES[1]
                ? representativeVariant.attributes.find((attr) =>
                    COLOR_ATTRIBUTES.includes(attr.name),
                  )?.label
                : undefined;

            const isOutOfStock = !!findOutOfStock(representativeVariant.attributes);

            return !isOutOfStock ? (
              <div
                className={classNames(styles.aov, {
                  [styles.selected]: hasFirstUnitSelected,
                })}
                key={item.productId}
              >
                <div
                  onClick={() =>
                    onChangeSKUSelectedForIndex({
                      sku: selectedSKUsForProduct[0]
                        ? undefined
                        : representativeVariant?.sku,
                      index: 0,
                      productId: item.productId,
                    })
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onChangeSKUSelectedForIndex({
                        sku: selectedSKUsForProduct[0]
                          ? undefined
                          : representativeVariant?.sku,
                        index: 0,
                        productId: item.productId,
                      });
                    }
                  }}
                  aria-label='Select or Deselect item'
                  role='button'
                  tabIndex={0}
                >
                  <div
                    className={styles['aov-image-container']}
                    data-testid='aov-image-container'
                  >
                    <img
                      src={representativeVariant.image}
                      alt={item.productName[locale]}
                    />
                    <div
                      className={classNames(styles['action-icon'], {
                        [styles.selected]: hasFirstUnitSelected,
                        [styles.select]: !hasFirstUnitSelected,
                      })}
                      data-testid='action-icon'
                    />
                  </div>
                  <div
                    className={styles['aov-details']}
                    data-testid='aov-details'
                  >
                    <div
                      className={styles['item-title']}
                      data-testid='item-title'
                    >
                      {item.badgeText?.[locale] && (
                        <div className={styles.badge}>
                          {item.badgeText?.[locale]}
                        </div>
                      )}
                      <div
                        className={styles['item-name']}
                        data-testid='item-name'
                      >
                        {item.productName[locale]}
                      </div>
                      <div
                        className={styles['item-attribute']}
                        data-testid='item-attribute'
                      >
                        {size?.label}
                        {color && size?.label && ` | ${color}`}
                      </div>
                      {isFreeGiftItem && isFreeGiftAndQualified ? (
                        <div className={styles.price}>
                          <div
                            className={classNames(
                              styles['original-price'],
                              styles.strikethrough,
                            )}
                            data-testid='original-price'
                          >
                            {priceToString({
                              price: {
                                ...price,
                                centAmount:
                                  price.centAmount +
                                  (discount?.centAmount || 0),
                              },
                              maximumFractionDigits: 0,
                              formatNumber,
                              formatMessage,
                            })}
                          </div>
                          <div
                            className={styles['promo-price']}
                            data-testid='promo-price'
                          >
                            {priceToString({
                              price: {
                                centAmount: 0,
                              },
                              maximumFractionDigits: 0,
                              formatNumber,
                              formatMessage,
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className={styles.price}>
                          <div
                            className={classNames(styles['original-price'], {
                              [styles.strikethrough]: haveDiscount,
                            })}
                            data-testid='original-price'
                          >
                            {priceToString({
                              price: haveDiscount
                                ? {
                                    ...price,
                                    centAmount:
                                      price.centAmount +
                                      (discount?.centAmount || 0),
                                  }
                                : price,
                              maximumFractionDigits: 0,
                              formatNumber,
                              formatMessage,
                            })}
                          </div>
                          {haveDiscount && (
                            <div
                              className={styles['promo-price']}
                              data-testid='promo-price'
                            >
                              {priceToString({
                                price,
                                maximumFractionDigits: 0,
                                formatNumber,
                                formatMessage,
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {(aovType === AOV_FLAT_RATE_TYPE ||
                  aovType === COMPLETE_YOUR_SET_TYPE) && (
                  <div
                    className={styles['quantity-container']}
                    data-testid='quantity-container'
                  >
                    <QuantityCounter
                      theme='cart'
                      quantity={quantitiesForProducts[item.productId] || 1}
                      isDecrementButtonDisabled={
                        (quantitiesForProducts[item.productId] || 1) <= 1
                      }
                      onQuantityIncrement={() => {
                        if (!hasFirstUnitSelected) {
                          onChangeSKUSelectedForIndex({
                            sku: representativeVariant?.sku,
                            index: 0,
                            productId: item.productId,
                          });
                        }
                        onQuantityIncrement(item.productId);
                      }}
                      onQuantityDecrement={() => {
                        onQuantityDecrement(item.productId);
                      }}
                    />
                  </div>
                )}
              </div>
            ) : null;
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default AOVVerticalCards;
